import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Save, Lock } from "lucide-react";
import { productAPI, fpirAPI } from "../../services/api";

const todayStr = () => new Date().toISOString().slice(0, 10);

const INPROCESS_LABELS = [
  { key: "first_piece", label: "First Piece" },
  { key: "inprocess_1", label: "8:00–10:00" },
  { key: "inprocess_2", label: "10:00–12:00" },
  { key: "inprocess_3", label: "12:00–2:00" },
  { key: "inprocess_4", label: "2:00–4:00" },
  { key: "inprocess_5", label: "4:00–6:00" },
  { key: "inprocess_6", label: "6:00–8:00" },
  { key: "last_piece", label: "Last Piece" },
];

export default function FpirForm({ basePath = "/company/fpir" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEditRoute = location.pathname.endsWith("/edit"); // /fpir/:id/edit
  const isAddMode = !id;
  const isLoadExisting = Boolean(id); // view OR edit both load an existing report

  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("Draft");

  const [header, setHeader] = useState({
    customer: "",
    rw_material: "",
    rm_grade: "",
    rm_colour_batch_no: "",
    machine: "",
    cycle_time: "",
    no_of_cavities: "",
    date: todayStr(),
    shift: "",
    incharge_sign: "",
    operator: "",
    update_on: "",
    description: "",
  });
  const [checkedBy, setCheckedBy] = useState("");
  const [approvedBy, setApprovedBy] = useState("");

  const [loading, setLoading] = useState(isLoadExisting);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Final reports are read-only no matter what route was hit (defense in depth —
  // backend already rejects the edit call too, this just avoids showing enabled
  // inputs that would fail on submit).
  const isReadOnly = isLoadExisting && (!isEditRoute || status === "Final");

  // ── Add mode: load product list, then parameters when a product is picked ──
  useEffect(() => {
    if (isLoadExisting) return;
    productAPI.list().then((r) => {
      const active = (r.data || []).filter((p) => p.status === "Active");
      setProducts(active);
    });
  }, [isLoadExisting]);

  const loadParameters = useCallback((pid) => {
    if (!pid) {
      setItems([]);
      return;
    }
    fpirAPI.listParameters(pid).then((r) => {
      const activeParams = (r.data || []).filter((p) => p.status === "Active");
      setItems(
        activeParams.map((p) => ({
          parameter: p.parameter,
          instrument: p.instrument,
          first_piece: "",
          inprocess_1: "",
          inprocess_2: "",
          inprocess_3: "",
          inprocess_4: "",
          inprocess_5: "",
          inprocess_6: "",
          last_piece: "",
        })),
      );
    });
  }, []);

  useEffect(() => {
    if (isAddMode) loadParameters(productId);
  }, [productId, isAddMode, loadParameters]);

  // ── View/Edit mode: load the saved report ──
  useEffect(() => {
    if (!isLoadExisting) return;
    setLoading(true);
    fpirAPI
      .getReport(id)
      .then((r) => {
        const report = r.data;
        setStatus(report.status);
        setProductId(String(report.product_id));
        setProducts([
          {
            id: report.product_id,
            name: report.Product?.name,
            code: report.Product?.code,
          },
        ]);
        setHeader({
          customer: report.customer || "",
          rw_material: report.rw_material || "",
          rm_grade: report.rm_grade || "",
          rm_colour_batch_no: report.rm_colour_batch_no || "",
          machine: report.machine || "",
          cycle_time: report.cycle_time || "",
          no_of_cavities: report.no_of_cavities ?? "",
          date: report.date,
          shift: report.shift || "",
          incharge_sign: report.incharge_sign || "",
          operator: report.operator || "",
          update_on: report.update_on || "",
          description: report.description || "",
        });
        setCheckedBy(report.checked_by || "");
        setApprovedBy(report.approved_by || "");
        setItems((report.items || []).map((it) => ({ ...it })));
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [id, isLoadExisting]);

  const updateItem = (i, field, value) => {
    setItems((prev) =>
      prev.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)),
    );
  };

  const submit = async () => {
    setErr("");
    if (!productId) return setErr("Select a part");
    if (items.length === 0)
      return setErr("No parameters found — add some in FPIR Master first");

    setSaving(true);
    try {
      const payload = {
        ...header,
        no_of_cavities: header.no_of_cavities ? +header.no_of_cavities : null,
        update_on: header.update_on || null,
        description: header.description || null,
        checked_by: checkedBy,
        approved_by: approvedBy,
        items,
      };
      if (isEditRoute) {
        await fpirAPI.updateReport(id, payload);
      } else {
        await fpirAPI.createReport({ product_id: productId, ...payload });
      }
      navigate(basePath);
    } catch (e) {
      setErr(e.message || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    if (
      !window.confirm(
        "Finalize this FPIR report? Once finalized it can NEVER be edited again, by anyone.",
      )
    )
      return;
    setSaving(true);
    try {
      await fpirAPI.finalizeReport(id);
      setStatus("Final");
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedProduct = products.find(
    (p) => String(p.id) === String(productId),
  );

  if (loading)
    return <div style={{ padding: 28, color: "var(--text2)" }}>Loading...</div>;

  return (
    <div style={{ padding: 28 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <button onClick={() => navigate(basePath)} style={iconBtn}>
          <ArrowLeft size={16} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>
            {isAddMode
              ? "Add FPIR"
              : isReadOnly
                ? "FPIR Report (View)"
                : "Edit FPIR Report"}
          </h1>
          <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            First / Inprocess / Last Piece Inspection Report
          </p>
        </div>
        {isLoadExisting && (
          <span
            className={`badge ${status === "Final" ? "badge-success" : "badge-danger"}`}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            {status === "Final" && <Lock size={11} />} {status}
          </span>
        )}
        {isLoadExisting && status === "Draft" && !isEditRoute && (
          <button
            className="btn-secondary"
            onClick={() => navigate(`${basePath}/${id}/edit`)}
          >
            Edit
          </button>
        )}
        {isLoadExisting && status === "Draft" && (
          <button
            className="btn-primary"
            onClick={finalize}
            disabled={saving}
            style={{
              background: "var(--danger)",
              borderColor: "var(--danger)",
            }}
          >
            Finalize (Lock)
          </button>
        )}
      </div>

      {err && (
        <div
          className="card"
          style={{ color: "var(--danger)", marginBottom: 16 }}
        >
          {err}
        </div>
      )}
      {isLoadExisting && status === "Final" && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            color: "var(--text2)",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Lock size={13} /> This report is Final and locked — no further edits
          are possible.
        </div>
      )}

      {/* Product + header fields */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
            gap: 14,
          }}
        >
          <div className="form-group">
            <label>Part</label>
            {!isAddMode ? (
              <input
                type="text"
                disabled
                value={`${selectedProduct?.name || ""} (${selectedProduct?.code || ""})`}
              />
            ) : (
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
              >
                <option value="">— Select part —</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.code})
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="form-group">
            <label>Customer</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={header.customer}
              onChange={(e) =>
                setHeader({ ...header, customer: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Raw Material</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={header.rw_material}
              onChange={(e) =>
                setHeader({ ...header, rw_material: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>RM Grade</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={header.rm_grade}
              onChange={(e) =>
                setHeader({ ...header, rm_grade: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>RM Colour / Batch No.</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={header.rm_colour_batch_no}
              onChange={(e) =>
                setHeader({ ...header, rm_colour_batch_no: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Machine</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={header.machine}
              onChange={(e) =>
                setHeader({ ...header, machine: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Cycle Time</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={header.cycle_time}
              onChange={(e) =>
                setHeader({ ...header, cycle_time: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>No. of Cavities</label>
            <input
              type="number"
              disabled={isReadOnly}
              value={header.no_of_cavities}
              onChange={(e) =>
                setHeader({ ...header, no_of_cavities: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              disabled={isReadOnly}
              value={header.date}
              max={todayStr()}
              onChange={(e) => setHeader({ ...header, date: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Shift</label>
            <input
              type="text"
              placeholder="e.g. A / B / C"
              disabled={isReadOnly}
              value={header.shift}
              onChange={(e) => setHeader({ ...header, shift: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Operator</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={header.operator}
              onChange={(e) =>
                setHeader({ ...header, operator: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Incharge Sign</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={header.incharge_sign}
              onChange={(e) =>
                setHeader({ ...header, incharge_sign: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Update On (optional)</label>
            <input
              type="date"
              disabled={isReadOnly}
              value={header.update_on}
              onChange={(e) =>
                setHeader({ ...header, update_on: e.target.value })
              }
            />
          </div>
        </div>
        <div className="form-group" style={{ marginTop: 14 }}>
          <label>Description (optional)</label>
          <textarea
            rows={2}
            disabled={isReadOnly}
            value={header.description}
            onChange={(e) =>
              setHeader({ ...header, description: e.target.value })
            }
          />
        </div>
      </div>

      {/* Parameter observations grid — 8 named shift-timeline slots */}
      <div
        className="card"
        style={{ padding: 0, overflow: "hidden", marginBottom: 20 }}
      >
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>S.N.</th>
                <th style={{ minWidth: 180 }}>Parameter</th>
                <th style={{ width: 110 }}>Instrument</th>
                {INPROCESS_LABELS.map((c) => (
                  <th key={c.key} style={{ width: 80 }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={3 + INPROCESS_LABELS.length}
                    style={{
                      textAlign: "center",
                      padding: 20,
                      color: "var(--text3)",
                    }}
                  >
                    {isAddMode
                      ? "Select a part to load its checklist"
                      : "No items"}
                  </td>
                </tr>
              )}
              {items.map((row, i) => (
                <tr key={i}>
                  <td className="text3">{i + 1}</td>
                  <td style={{ fontSize: 12 }}>{row.parameter}</td>
                  <td className="text2" style={{ fontSize: 12 }}>
                    {row.instrument}
                  </td>
                  {INPROCESS_LABELS.map((c) => (
                    <td key={c.key}>
                      <input
                        type="text"
                        disabled={isReadOnly}
                        value={row[c.key] || ""}
                        onChange={(e) => updateItem(i, c.key, e.target.value)}
                        style={{
                          width: "100%",
                          padding: "4px 6px",
                          fontSize: 12,
                          textAlign: "center",
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sign-off */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
        >
          <div className="form-group">
            <label>Checked By</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={checkedBy}
              onChange={(e) => setCheckedBy(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Approved By</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={approvedBy}
              onChange={(e) => setApprovedBy(e.target.value)}
            />
          </div>
        </div>
      </div>

      {!isReadOnly && (
        <button
          className="btn-primary"
          onClick={submit}
          disabled={saving}
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <Save size={15} />{" "}
          {saving ? "Saving..." : isEditRoute ? "Save Changes" : "Submit FPIR"}
        </button>
      )}
    </div>
  );
}

const iconBtn = {
  background: "var(--surface2)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: 6,
  cursor: "pointer",
  color: "var(--text2)",
  display: "flex",
};
